import { useMutation, useQueryClient, useQuery } from "react-query";
import { clearStoredAuth } from "../utils";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, ChangeEvent } from "react";
import { API_URL } from "../config";
import { EMPTY_OPERATOR } from "../constants";
import { getOperatorBookedOptions, validateEmail } from "../utils";

const extractBookedOptions = (operator: Operator) =>
  getOperatorBookedOptions(operator);

const mapOperatorForState = (operator: Operator) => {
  const bookedOptions = extractBookedOptions(operator);
  const hasSettings = operator?.settings || bookedOptions.length;
  const settings = hasSettings
    ? { ...(operator.settings || {}), booked_options: bookedOptions }
    : (operator.settings || null);

  return {
    ...operator,
    settings: settings || null,
    booked_options: bookedOptions,
  };
};

const createHttpError = (message: string, status: number): HttpError => {
  const error = new Error(message) as HttpError;
  error.status = status;
  return error;
};

const isUnauthorizedError = (error: HttpError) =>
  error.status === 401 || error.code === 401;

const postOperator = async (operator: Operator) => {
  const res = await fetch(`${API_URL}/operators`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(operator),
    credentials: "include",
  });

  if (!res.ok) {
    throw createHttpError(res.statusText, res.status);
  }

  return res.json();
};

export const usePostOperator = () => {
  const [operator, setOperator] = useState(mapOperatorForState(EMPTY_OPERATOR));
  const [error, setError] = useState("");
  const [message, setMesssage] = useState("");

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation(postOperator, {
    onSuccess: (resOperator: Operator) => {
      queryClient.invalidateQueries('operators');
      queryClient.invalidateQueries(['admin-email-distribution-operators']);
      queryClient.invalidateQueries(['admin-leon-operators']);
      setOperator(mapOperatorForState(resOperator));
      setError("");
      setMesssage(`Operator ${operator.name} has been added!`);
    },
    onError: (err: HttpError) => {
      if (isUnauthorizedError(err)) {
        clearStoredAuth();
        navigate('/');
      }

      setError("You can't add this operator");
      setMesssage("");
    },
  });

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, name: e.target.value});
    setError("");
    setMesssage("");
  };

  const handleCompanyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, company: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleIcaoChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, icao_code: e.target.value });
    setError("");
    setMesssage("");
  };


  const handleContactChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, contact_name: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, email: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleTelChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, tel_no: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleDashboardEnabledChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, is_dashboard_enabled: e.target.checked });
    setError("");
    setMesssage("");
  };

  const onSubmit = () => {
    setError("");
    setMesssage("");
    if (!operator.name?.length) {
      setError("Please enter a valid name");
      return;
    }
    if (!operator.company?.length) {
      setError("Please enter a valid company");
      return;
    }
    if (operator.icao_code?.length !== 3) {
      setError("Please enter a valid ICAO");
      return;
    }
    if (!!operator.email && !validateEmail(operator.email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!!operator.tel_no && operator.tel_no?.length < 8) {
      setError("Please enter a valid phone number");
      return;
    }
    mutation.mutate(operator);
  }

  const handleBookedOptionsChange = (bookedOptions: string[]) => {
    // Always create a settings object if booked_options are set, or preserve existing settings
    const updatedSettings = {
      ...(operator.settings || {}),
      booked_options: bookedOptions,
    };

    setOperator({
      ...operator,
      booked_options: bookedOptions,
      // Always include settings object if we have booked_options or existing settings
      settings: (bookedOptions.length > 0 || operator.settings) ? updatedSettings : null,
    });
    setError("");
    setMesssage("");
  };

  return {
    operator,
    setOperator,
    handleNameChange,
    handleCompanyChange,
    handleIcaoChange,
    handleContactChange,
    handleEmailChange,
    handleTelChange,
    handleDashboardEnabledChange,
    handleBookedOptionsChange,
    onSubmit,
    isLoading: mutation.isLoading,
    error,
    message,
  };
};

const putOperator = async (operator: Operator) => {
  const res = await fetch(`${API_URL}/operators/${operator.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(operator),
    credentials: "include",
  });

  if (!res.ok) {
    throw createHttpError(res.statusText, res.status);
  }

  return res.json();
};

export const usePutOperator = (selectedOperator: Operator) => {
  const [operator, setOperator] = useState(mapOperatorForState(selectedOperator));
  const [error, setError] = useState("");
  const [message, setMesssage] = useState("");

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    setOperator(mapOperatorForState(selectedOperator));
  }, [selectedOperator]);

  const mutation = useMutation(putOperator, {
    onSuccess: (resOperator: Operator) => {
      // Invalidate all related queries to ensure UI is updated
      queryClient.invalidateQueries('operators');
      queryClient.invalidateQueries(['operator-summary']);
      queryClient.invalidateQueries(['admin-operators']);
      queryClient.invalidateQueries(['admin-operators', selectedOperator.id]);
      // Invalidate operator lists used by feature-specific admin pages
      queryClient.invalidateQueries(['admin-email-distribution-operators']);
      queryClient.invalidateQueries(['admin-leon-operators']);
      // Update local state with the response from server (contains latest data from DB)
      const mappedOperator = mapOperatorForState(resOperator);
      setOperator(mappedOperator);
      setError("");
      setMesssage(`Operator ${resOperator.name || operator.name} has been updated!`);
    },
    onError: (err: HttpError) => {
      if (isUnauthorizedError(err)) {
        clearStoredAuth();
        navigate('/');
      }

      setError("You can't update this operator");
      setMesssage("");
    },
  });

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, name: e.target.value});
    setError("");
    setMesssage("");
  };

  const handleCompanyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, company: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleIcaoChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, icao_code: e.target.value });
    setError("");
    setMesssage("");
  };


  const handleContactChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, contact_name: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, email: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleTelChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, tel_no: e.target.value });
    setError("");
    setMesssage("");
  };

  const handleDashboardEnabledChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOperator({ ...operator, is_dashboard_enabled: e.target.checked });
    setError("");
    setMesssage("");
  };

  const handleBookedOptionsChange = (bookedOptions: string[]) => {
    // Always create a settings object if booked_options are set, or preserve existing settings
    const updatedSettings = {
      ...(operator.settings || {}),
      booked_options: bookedOptions,
    };

    setOperator({
      ...operator,
      booked_options: bookedOptions,
      // Always include settings object if we have booked_options or existing settings
      settings: (bookedOptions.length > 0 || operator.settings) ? updatedSettings : null,
    });
    setError("");
    setMesssage("");
  };

  const onSubmit = () => {
    setError("");
    setMesssage("");
    if (!operator.name?.length) {
      setError("Please enter a valid name");
      return;
    }
    if (!operator.company?.length) {
      setError("Please enter a valid company");
      return;
    }
    if (operator.icao_code?.length !== 3) {
      setError("Please enter a valid ICAO");
      return;
    }
    if (!!operator.email && !validateEmail(operator.email)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!!operator.tel_no && operator.tel_no?.length < 8) {
      setError("Please enter a valid phone number");
      return;
    }
    
    // Ensure settings and booked_options are properly included when submitting
    const operatorToSubmit = {
      ...operator,
      // Ensure settings object is included if we have booked_options or existing settings
      settings: (operator.settings || (operator.booked_options && operator.booked_options.length > 0)) 
        ? { ...(operator.settings || {}), booked_options: operator.booked_options || [] }
        : null,
      booked_options: operator.booked_options || [],
    };
    
    mutation.mutate(operatorToSubmit);
  }

  return {
    operator,
    setOperator,
    handleNameChange,
    handleCompanyChange,
    handleIcaoChange,
    handleContactChange,
    handleEmailChange,
    handleTelChange,
    handleDashboardEnabledChange,
    handleBookedOptionsChange,
    onSubmit,
    isLoading: mutation.isLoading,
    error,
    message,
  };
};

const fetchOperators = async (): Promise<Operator[]> => {
  const res = await fetch(`${API_URL}/operators`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

export const useOperators = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(EMPTY_OPERATOR);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpenEditModal = (operator: Operator) => {
    if (operator.id) {
      setSelectedOperator(mapOperatorForState(operator));
      setIsEditModalOpen(true);
    }
  }

  const operators = useQuery<Operator[], Error>(["operators"], () => fetchOperators(), {
    refetchOnWindowFocus: false,
    retry: 1,
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  })

  return {
    operators,
    setIsAddModalOpen,
    isAddModalOpen,
    setIsEditModalOpen,
    handleOpenEditModal,
    isEditModalOpen,
    selectedOperator,
  };
};

export const useAllOperatorsForConsistency = () => {
  const navigate = useNavigate();

  return useQuery(["operators-consistency"], () => fetchOperators(), {
    refetchOnWindowFocus: false,
    retry: 1,
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};

const fetchOperatorOptions = async () => {
  const res = await fetch(`${API_URL}/admin/options`, {
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

export const useOperatorOptionsList = () => {
  const navigate = useNavigate();

  return useQuery(["operator-options"], () => fetchOperatorOptions(), {
    refetchOnWindowFocus: false,
    retry: 1,
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};

type UpdateOperatorOptionsPayload = {
  operatorId: number;
  bookedOptions: string[];
};

type PatchOperatorSettingPayload = {
  operatorId: number;
  path: string;
  value: unknown;
  mode?: "set" | "set_if_missing";
  source?: string | null;
};

const updateOperatorOptions = async ({ operatorId, bookedOptions }: UpdateOperatorOptionsPayload) => {
  const res = await fetch(`${API_URL}/admin/operators/${operatorId}/options`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ booked_options: bookedOptions }),
    credentials: "include",
  });

  if (!res.ok) {
    let errorMessage = res.statusText;
    try {
      const errorData = await res.json();
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.text) {
        errorMessage = errorData.text;
      }
    } catch (e) {
      // If response is not JSON, use status text
    }
    throw createHttpError(errorMessage, res.status);
  }

  return res.json();
};

export const useUpdateOperatorBookedOptions = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(updateOperatorOptions, {
    onSuccess: (data, variables) => {
      // Invalidate all related queries to ensure UI is updated
      queryClient.invalidateQueries('operators');
      queryClient.invalidateQueries(['admin-operators']);
      queryClient.invalidateQueries(['operator-summary']);
      // Also invalidate the specific operator summary query
      queryClient.invalidateQueries(['operator-summary', variables.operatorId]);
      // Invalidate operator lists used by feature-specific admin pages
      queryClient.invalidateQueries(['admin-email-distribution-operators']);
      queryClient.invalidateQueries(['admin-leon-operators']);
    },
    onError: (err: HttpError) => {
      if (isUnauthorizedError(err)) {
        clearStoredAuth();
        navigate('/');
      }
    },
  });
};

const patchOperatorSetting = async ({
  operatorId,
  path,
  value,
  mode = "set",
  source = null,
}: PatchOperatorSettingPayload) => {
  const res = await fetch(`${API_URL}/admin/operators/${operatorId}/settings`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ path, value, mode, source }),
    credentials: "include",
  });

  if (!res.ok) {
    let errorMessage = res.statusText;
    try {
      const errorData = await res.json();
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.text) {
        errorMessage = errorData.text;
      }
    } catch (_error) {
      // Ignore parse errors and fall back to status text.
    }
    throw createHttpError(errorMessage, res.status);
  }

  return res.json();
};

export const usePatchOperatorSetting = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(patchOperatorSetting, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries("operators");
      queryClient.invalidateQueries(["operators-consistency"]);
      queryClient.invalidateQueries(["admin-operators"]);
      queryClient.invalidateQueries(["operator-summary"]);
      queryClient.invalidateQueries(["operator-summary", variables.operatorId]);
    },
    onError: (err: HttpError) => {
      if (isUnauthorizedError(err)) {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};

const fetchOperatorLogo = async (operatorId: number) => {
  const res = await fetch(`${API_URL}/admin/operators/${operatorId}/logo`, {
    credentials: "include",
  });

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.blob();
};

export const useOperatorLogo = (operatorId: number | null) => {
  const navigate = useNavigate();

  return useQuery<Blob | null, Error>(["operator-logo", operatorId], () => fetchOperatorLogo(operatorId || 0), {
    enabled: !!operatorId,
    refetchOnWindowFocus: false,
    retry: 1,
    onError: (error: Error) => {
      if (error.message === "401") {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};

type UploadOperatorLogoPayload = {
  operatorId: number;
  file: File;
};

const uploadOperatorLogo = async ({ operatorId, file }: UploadOperatorLogoPayload) => {
  const formData = new FormData();
  formData.append("logo", file);

  const res = await fetch(`${API_URL}/admin/operators/${operatorId}/logo`, {
    method: "PUT",
    body: formData,
    credentials: "include",
  });

  if (!res.ok) {
    throw createHttpError(res.statusText, res.status);
  }

  if (res.status === 204) {
    return null;
  }

  const contentType = res.headers.get("content-type");

  if (contentType && contentType.includes("application/json")) {
    return res.json();
  }

  return null;
};

export const useUploadOperatorLogo = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<unknown, HttpError, UploadOperatorLogoPayload>(uploadOperatorLogo, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["operator-logo", variables.operatorId]);
    },
    onError: (err: HttpError) => {
      if (isUnauthorizedError(err)) {
        clearStoredAuth();
        navigate("/");
      }
    },
  });
};

// Message settings hooks

type MessageSettings = {
  operator_id: number;
  messaging_enabled: boolean;
};

const fetchOperatorMessageSettings = async (operatorId: number): Promise<MessageSettings> => {
  const res = await fetch(`${API_URL}/ops/messaging-status?operatorIds=${operatorId}`, {
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  const payload = await res.json();
  const settings = payload?.settings || {};
  return {
    operator_id: operatorId,
    messaging_enabled: Boolean(settings[operatorId]),
  };
};

export const useOperatorMessageSettings = (operatorId: number | null) => {
  const navigate = useNavigate();

  return useQuery<MessageSettings, Error>(
    ["operator-message-settings", operatorId],
    () => fetchOperatorMessageSettings(operatorId!),
    {
      enabled: !!operatorId,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: Error) => {
        if (error.message === "401") {
          clearStoredAuth();
          navigate("/");
        }
      },
    }
  );
};

type UpdateMessageSettingsPayload = {
  operatorId: number;
  messagingEnabled: boolean;
};

const updateOperatorMessageSettings = async ({
  operatorId,
  messagingEnabled,
}: UpdateMessageSettingsPayload): Promise<MessageSettings> => {
  const res = await fetch(`${API_URL}/admin/operators/${operatorId}/message-settings`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ messaging_enabled: messagingEnabled }),
    credentials: "include",
  });

  if (!res.ok) {
    throw createHttpError(res.statusText, res.status);
  }

  return res.json();
};

export const useUpdateOperatorMessageSettings = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<MessageSettings, HttpError, UpdateMessageSettingsPayload>(
    updateOperatorMessageSettings,
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(["operator-message-settings", variables.operatorId]);
        queryClient.invalidateQueries(["operators-message-settings"]);
      },
    onError: (err: HttpError) => {
        if (isUnauthorizedError(err)) {
          clearStoredAuth();
          navigate("/");
        }
      },
    }
  );
};

type OperatorsMessageSettings = {
  settings: Record<number, boolean>;
};

const fetchOperatorsMessageSettings = async (operatorIds: number[]): Promise<OperatorsMessageSettings> => {
  if (!operatorIds.length) {
    return { settings: {} };
  }

  // Use ops endpoint (accessible by all authenticated users)
  const res = await fetch(
    `${API_URL}/ops/messaging-status?operatorIds=${operatorIds.join(",")}`,
    {
      credentials: "include",
    }
  );

  if (!res.ok) {
    // Return empty settings on error
    return { settings: {} };
  }

  return res.json();
};

export const useOperatorsMessageSettings = (operatorIds: number[]) => {
  const navigate = useNavigate();
  const uniqueIds = Array.from(new Set(operatorIds.filter((id) => id !== null)));

  return useQuery<OperatorsMessageSettings, Error>(
    ["operators-message-settings", uniqueIds.sort().join(",")],
    () => fetchOperatorsMessageSettings(uniqueIds),
    {
      enabled: uniqueIds.length > 0,
      refetchOnWindowFocus: false,
      retry: false, // Don't retry - non-admin users will get 403
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );
};
